/**
 * Lifetime / days / none warranty helpers.
 * Use these everywhere — never compare expiresAt alone for coverage.
 */

export type WarrantyType = 'days' | 'lifetime' | 'none';

export type ResolvedWarranty = {
  type: WarrantyType;
  /** Days when type === 'days'; otherwise null */
  days: number | null;
  /** Null for lifetime and none */
  expiresAt: Date | null;
  source: string;
};

export function isWarrantyType(value: unknown): value is WarrantyType {
  return value === 'days' || value === 'lifetime' || value === 'none';
}

/** Infer type from legacy day/expiry fields when type is missing on old rows. */
export function normalizeWarrantyType(
  type: string | null | undefined,
  days?: number | null,
  expiresAt?: Date | string | null,
): WarrantyType {
  if (isWarrantyType(type)) return type;
  if (expiresAt) return 'days';
  if (days != null && Number(days) > 0) return 'days';
  return 'none';
}

export function computeWarrantyExpiresAt(
  type: WarrantyType,
  days: number | null | undefined,
  from: Date = new Date(),
): Date | null {
  if (type === 'lifetime' || type === 'none') return null;
  const d = Number(days ?? 0);
  if (!(d > 0)) return null;
  const expires = new Date(from.getTime());
  expires.setUTCDate(expires.getUTCDate() + Math.trunc(d));
  return expires;
}

/**
 * Is coverage still active?
 * Lifetime is always active. None is never. Days requires a future expiresAt.
 */
export function isUnderWarranty(params: {
  type?: string | null;
  days?: number | null;
  expiresAt?: Date | string | null;
  now?: Date;
}): boolean {
  const type = normalizeWarrantyType(params.type, params.days, params.expiresAt);
  if (type === 'lifetime') return true;
  if (type === 'none') return false;
  if (!params.expiresAt) return false;
  const exp = params.expiresAt instanceof Date ? params.expiresAt : new Date(params.expiresAt);
  if (Number.isNaN(exp.getTime())) return false;
  return exp.getTime() > (params.now ?? new Date()).getTime();
}

/** Short display, e.g. "Lifetime warranty" / "90 day warranty" / "No warranty". */
export function formatWarrantySummary(params: {
  type?: string | null;
  days?: number | null;
  expiresAt?: Date | string | null;
}): string {
  const type = normalizeWarrantyType(params.type, params.days, params.expiresAt);
  if (type === 'lifetime') return 'Lifetime warranty';
  if (type === 'none') return 'No warranty';
  const days = Number(params.days ?? 0);
  if (days > 0) return `${days} day warranty`;
  if (params.expiresAt) return 'Limited warranty';
  return 'No warranty';
}

/**
 * Full label with optional expiry.
 * "90 day warranty · expires Oct 24, 2026"
 * "Lifetime warranty"
 * "No warranty"
 */
export function formatWarrantyDetail(params: {
  type?: string | null;
  days?: number | null;
  expiresAt?: Date | string | null;
  formatDate?: (d: Date) => string;
}): string {
  const type = normalizeWarrantyType(params.type, params.days, params.expiresAt);
  if (type === 'lifetime') return 'Lifetime warranty';
  if (type === 'none') return 'No warranty';
  const days = Number(params.days ?? 0);
  const base = days > 0 ? `${days} day warranty` : 'Limited warranty';
  if (!params.expiresAt) return base;
  const exp = params.expiresAt instanceof Date ? params.expiresAt : new Date(params.expiresAt);
  if (Number.isNaN(exp.getTime())) return base;
  const fmt =
    params.formatDate ??
    ((d: Date) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }));
  return `${base} · expires ${fmt(exp)}`;
}

/** Group for expiry reports: lifetime rows never sort with null dates. */
export type WarrantyReportBucket = 'expiring' | 'expired' | 'lifetime' | 'none';

export function warrantyReportBucket(params: {
  type?: string | null;
  days?: number | null;
  expiresAt?: Date | string | null;
  now?: Date;
}): WarrantyReportBucket {
  const type = normalizeWarrantyType(params.type, params.days, params.expiresAt);
  if (type === 'lifetime') return 'lifetime';
  if (type === 'none') return 'none';
  if (isUnderWarranty(params)) return 'expiring';
  return 'expired';
}

/** Receipt line — never blank for lifetime, never invent a date. */
export function formatWarrantyForReceipt(params: {
  type?: string | null;
  days?: number | null;
  expiresAt?: Date | string | null;
  formatDate?: (d: Date) => string;
}): string {
  return formatWarrantyDetail(params);
}

/** Snapshot pair for DB write. */
export function snapshotWarranty(params: {
  type: WarrantyType;
  days?: number | null;
  from?: Date;
}): { warrantyType: WarrantyType; warrantyDays: number; warrantyExpiresAt: Date | null } {
  const type = params.type;
  if (type === 'lifetime') {
    return { warrantyType: 'lifetime', warrantyDays: 0, warrantyExpiresAt: null };
  }
  if (type === 'none') {
    return { warrantyType: 'none', warrantyDays: 0, warrantyExpiresAt: null };
  }
  const days = Math.max(0, Math.trunc(Number(params.days ?? 0)));
  return {
    warrantyType: 'days',
    warrantyDays: days,
    warrantyExpiresAt: computeWarrantyExpiresAt('days', days, params.from ?? new Date()),
  };
}
